import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BasePage from './BasePage';
import {
    Typography,
    Card,
    Row,
    Col,
    Tag,
    Space,
    Statistic,
    Tooltip,
    Spin,
    Button,
} from 'antd';
import {
    ThunderboltOutlined,
    EnvironmentOutlined,
    SyncOutlined,
    RightOutlined,
    BankOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { sitesService, empresaClientesService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import OperacaoSitesMap from '../components/MapComponent/OperacaoSitesMap';
import {
    getStatusColor,
    getStatusIcon,
    getStatusText,
    getPlantTypeIcon,
    getBatteryIcon,
    getBatteryStatusIcon,
} from '../utils/siteHelpers';

const { Title, Text } = Typography;

// ── Coordenadas fixas por site — mesma abordagem do DashboardMeteorologico ────
const COORDS_FIXAS = {
    fiap:    { lat: -23.57428, lng: -46.62329 },
    quarahy: { lat: -22.91456, lng: -43.22432 },
};

function applyFixedCoords(site) {
    const nome = (site?.name || '').toLowerCase();
    if (nome.includes('quarahy')) return { ...site, latitude: COORDS_FIXAS.quarahy.lat, longitude: COORDS_FIXAS.quarahy.lng };
    if (nome.includes('fiap'))    return { ...site, latitude: COORDS_FIXAS.fiap.lat,    longitude: COORDS_FIXAS.fiap.lng    };
    return site;
}

// Card individual de uma usina/site
function SiteCard({ site, onSiteClick, highlightedSiteId, setHighlightedSiteId }) {
    return (
        <Card
            size="small"
            hoverable
            onClick={() => onSiteClick(site)}
            onMouseEnter={() => setHighlightedSiteId(site.id)}
            onMouseLeave={() => setHighlightedSiteId(null)}
            style={{
                borderTop: `4px solid ${getStatusColor(site.realtime?.plantStatus)}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: highlightedSiteId === site.id ? '0 0 0 2px rgba(24,144,255,0.4)' : undefined,
                height: '100%',
            }}
        >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                    <span style={{ fontSize: 20 }}>{getPlantTypeIcon(site.plantType)}</span>
                    <div>
                        <Text strong style={{ display: 'block', fontSize: 13 }}>{site.name}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            <EnvironmentOutlined style={{ marginRight: 4 }} />
                            {site.city} - {site.state}
                        </Text>
                    </div>
                </Space>
                <Tooltip
                    title={
                        site.realtime?.plantStatus === 0 && site.realtime?.dailyGeneration > 0
                            ? 'Conectado. Gerou hoje.'
                            : site.realtime?.plantStatus === 1
                                ? 'Gerando'
                                : site.realtime?.plantStatus === -1
                                    ? 'Offline'
                                    : undefined
                    }
                >
                    <Tag color={getStatusColor(site.realtime?.plantStatus)} style={{ fontSize: 11 }}>
                        {getStatusIcon(site.realtime?.plantStatus)}{' '}
                        {getStatusText(site.realtime?.plantStatus)}
                    </Tag>
                </Tooltip>
                <Row gutter={8}>
                    <Col span={12}>
                        <Statistic
                            title="Gerando"
                            value={site.realtime?.pvPower ? (site.realtime.pvPower / 1000).toFixed(1) : '-'}
                            suffix="kW"
                            valueStyle={{ fontSize: 15 }}
                        />
                    </Col>
                    <Col span={12}>
                        <Statistic
                            title="Hoje"
                            value={site.realtime?.dailyGeneration ? site.realtime.dailyGeneration.toFixed(1) : '-'}
                            suffix="kWh"
                            valueStyle={{ fontSize: 15, color: '#52c41a' }}
                        />
                    </Col>
                </Row>
                {site.realtime?.batterySoc1 != null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {getBatteryIcon(site.realtime.batterySoc1)}
                        <Text strong style={{ fontSize: 12 }}>{site.realtime.batterySoc1}%</Text>
                        {getBatteryStatusIcon(site.realtime.batteryStatus1)}
                    </div>
                )}
            </Space>
        </Card>
    );
}

// Card de grupo de empresa com Collapse interno
function EmpresaGroupCard({ empresaNome, sites, onSiteClick, highlightedSiteId, setHighlightedSiteId, defaultOpen }) {
    const [expanded, setExpanded] = useState(defaultOpen);

    const online = sites.filter(s => s.realtime?.plantStatus === 1).length;
    const totalPower = sites.reduce((sum, s) => sum + (s.realtime?.pvPower ? s.realtime.pvPower / 1000 : 0), 0);
    const hasBattery = sites.some(s => s.realtime?.batterySoc1 != null);
    const allStatuses = sites.map(s => s.realtime?.plantStatus);
    const anyOnline = allStatuses.some(s => s === 1 || s === 0);
    const allOffline = allStatuses.every(s => s === -1 || s == null);
    const headerColor = allOffline ? '#8c8c8c' : anyOnline ? '#52c41a' : '#1890ff';

    return (
        <Card
            style={{
                borderRadius: 10,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                border: `1px solid ${allOffline ? '#e0e0e0' : '#e6f7ff'}`,
                overflow: 'hidden',
                marginBottom: 0,
            }}
            bodyStyle={{ padding: 0 }}
        >
            {/* Header clicável do grupo */}
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    padding: '14px 20px',
                    background: allOffline
                        ? 'linear-gradient(90deg, #f5f5f5 0%, #fafafa 100%)'
                        : 'linear-gradient(90deg, #e6f7ff 0%, #f0f9ff 100%)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: expanded ? '1px solid #e8e8e8' : 'none',
                    transition: 'background 0.2s',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: allOffline ? '#f0f0f0' : '#1890ff22',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 20,
                        }}
                    >
                        <BankOutlined style={{ color: allOffline ? '#8c8c8c' : '#1890ff' }} />
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Tag color="blue" style={{ fontSize: 10, margin: 0, padding: '0 6px' }}>SITE</Tag>
                            <Text strong style={{ fontSize: 15, color: '#141414' }}>{empresaNome}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <TeamOutlined style={{ marginRight: 4 }} />
                                {sites.length} {sites.length === 1 ? 'usina' : 'usinas'}
                            </Text>
                            <Tag
                                color={headerColor}
                                style={{ fontSize: 11, lineHeight: '18px', padding: '0 6px', margin: 0 }}
                            >
                                {online} online
                            </Tag>
                            {hasBattery && (
                                <Tag color="purple" style={{ fontSize: 11, lineHeight: '18px', padding: '0 6px', margin: 0 }}>
                                    🔋 Bateria
                                </Tag>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {totalPower > 0 && (
                        <div style={{ textAlign: 'right' }}>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Geração total</Text>
                            <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                                {totalPower.toFixed(1)} kW
                            </Text>
                        </div>
                    )}
                    <div style={{ color: '#8c8c8c', fontSize: 14, transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'none' }}>
                        <RightOutlined />
                    </div>
                </div>
            </div>

            {/* Usinas expandidas */}
            {expanded && (
                <div style={{ padding: '16px 20px', background: '#fff' }}>
                    <Row gutter={[12, 12]}>
                        {sites.map(site => (
                            <Col key={site.id} xs={24} sm={12} lg={8}>
                                <SiteCard
                                    site={site}
                                    onSiteClick={onSiteClick}
                                    highlightedSiteId={highlightedSiteId}
                                    setHighlightedSiteId={setHighlightedSiteId}
                                />
                            </Col>
                        ))}
                    </Row>
                </div>
            )}
        </Card>
    );
}

const OperacaoPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [sites, setSites] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState('');
    const [highlightedSiteId, setHighlightedSiteId] = useState(null);

    const isAdmin = !user?.empresaClienteId;

    const fetchSites = useCallback(async () => {
        try {
            setLoading(true);
            const empresaId = user?.empresaClienteId || null;
            const response = await sitesService.getAllWithRealtime(empresaId);
            if (response?.success && response?.data) {
                // Aplica coordenadas fixas para sites conhecidos imediatamente
                const enriched = response.data.map(applyFixedCoords);
                setSites(enriched);
                setLastUpdate(new Date().toLocaleTimeString('pt-BR'));
            }
        } catch (error) {
            console.error('Erro ao carregar sites:', error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    const fetchEmpresas = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const data = await empresaClientesService.getAll();
            const list = Array.isArray(data) ? data : (data?.data || []);
            setEmpresas(list);
        } catch (e) {
            console.error('Erro ao buscar empresas:', e);
        }
    }, [isAdmin]);

    useEffect(() => {
        fetchSites();
        fetchEmpresas();
    }, [fetchSites, fetchEmpresas]);

    // Agrupa sites por empresaClienteId
    const siteGroups = useMemo(() => {
        const groups = {};
        for (const site of sites) {
            const key = site.empresaClienteId || 'sem-empresa';
            if (!groups[key]) groups[key] = [];
            groups[key].push(site);
        }
        return groups;
    }, [sites]);

    // Resolve nome da empresa pelo ID
    const getEmpresaNome = useCallback((id) => {
        if (!id || id === 'sem-empresa') return 'Sem empresa';
        const empresa = empresas.find(e => e.id === id);
        if (empresa) return empresa.nome;
        // Tenta inferir do nome das usinas
        const sitesDoGrupo = siteGroups[id] || [];
        const primeiroNome = sitesDoGrupo[0]?.name || '';
        if (primeiroNome.toLowerCase().includes('fiap')) return 'FIAP';
        if (primeiroNome.toLowerCase().includes('quarahy')) return 'Quarahy';
        return `Empresa ${id.slice(0, 8)}...`;
    }, [empresas, siteGroups]);

    const handleSiteClick = (site) => navigate(`/site/${site.id}`);

    const groupEntries = Object.entries(siteGroups);
    // Ordenar: empresas com mais usinas primeiro
    groupEntries.sort((a, b) => b[1].length - a[1].length);

    return (
        <BasePage>
            <div style={{ backgroundColor: '#FFF', padding: '24px', borderRadius: '8px' }}>
                {/* Cabeçalho */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 8 }}>
                    <Title level={2} style={{ margin: 0 }}>
                        <ThunderboltOutlined style={{ marginRight: 12, color: '#FB8500' }} />
                        Painel de Operação
                    </Title>
                    <Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>Atualizado: {lastUpdate}</Text>
                        <Button icon={<SyncOutlined spin={loading} />} onClick={fetchSites} loading={loading}>
                            Atualizar
                        </Button>
                    </Space>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: 80 }}>
                        <Spin size="large" />
                    </div>
                ) : sites.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 80, color: '#8c8c8c' }}>
                        <EnvironmentOutlined style={{ fontSize: 48, marginBottom: 16, display: 'block' }} />
                        Nenhum site cadastrado.
                        <br />
                        <Button type="link" onClick={() => navigate('/cadastros')} style={{ marginTop: 8 }}>
                            Ir para Cadastros
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* Admin: cards agrupados por empresa | Usuário: cards diretos */}
                        {isAdmin && groupEntries.length > 1 ? (
                            <Space direction="vertical" size={12} style={{ width: '100%', marginBottom: 24 }}>
                                {groupEntries.map(([empresaId, sitesDoGrupo], idx) => (
                                    <EmpresaGroupCard
                                        key={empresaId}
                                        empresaNome={getEmpresaNome(empresaId)}
                                        sites={sitesDoGrupo}
                                        onSiteClick={handleSiteClick}
                                        highlightedSiteId={highlightedSiteId}
                                        setHighlightedSiteId={setHighlightedSiteId}
                                        defaultOpen={false}
                                    />
                                ))}
                            </Space>
                        ) : (
                            // Usuário de empresa específica ou admin com apenas 1 grupo: lista plana
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                {sites.map((site) => (
                                    <Col key={site.id} xs={24} sm={12} lg={8}>
                                        <SiteCard
                                            site={site}
                                            onSiteClick={handleSiteClick}
                                            highlightedSiteId={highlightedSiteId}
                                            setHighlightedSiteId={setHighlightedSiteId}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        )}

                        {/* Mapa satélite */}
                        <Card
                            title={
                                <Space>
                                    <span>Mapa das Usinas</span>
                                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                                        🟢 Gerando · 🔵 Aguardando · 🔴 Offline
                                    </Text>
                                </Space>
                            }
                        >
                            <OperacaoSitesMap
                                sites={sites}
                                onSiteClick={handleSiteClick}
                                highlightedSiteId={highlightedSiteId}
                                height={380}
                            />
                        </Card>
                    </>
                )}
            </div>
        </BasePage>
    );
};

export default OperacaoPage;
